import { useCallback, useEffect, useState } from "react";

import { DateRange } from "react-day-picker";

import { format } from "date-fns";

import { DatePickerValue } from "./DatePicker.types";
import {  DateTimeChange, PickerStrategy } from "./DatePickerStrategy";
import { getCurrentTimeRounded,getDateRange, setSelectedDateTime } from "./utils";

export interface UseRangeDatePickerParams {
  value?: DatePickerValue;
  onChange?: (value?: DateRange) => void;
  onApply?: (value: DateRange) => void;
  onClear?: () => void;
  fixedRangeDays?: number;
  shouldExcludeFuture?: boolean;
  shouldExcludePast?: boolean;
  disableDatesAfter?: Date;
}

const DEFAULT_TIME = '00:00';

export const useRangeDatePicker = ({
  value,
  onChange,
  onApply,
  onClear,
  fixedRangeDays,
  shouldExcludeFuture,
  shouldExcludePast,
  disableDatesAfter,
}: UseRangeDatePickerParams): PickerStrategy => {

  const getDefaultTime = useCallback(() => {
    return shouldExcludePast ? getCurrentTimeRounded() : DEFAULT_TIME;
  }, [shouldExcludePast]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(value ? value as DateRange : undefined);
  const [fromTimeValue, setFromTimeValue] = useState(() => shouldExcludePast ? getCurrentTimeRounded() : DEFAULT_TIME);
  const [toTimeValue, setToTimeValue] = useState(() => shouldExcludePast ? getCurrentTimeRounded() : DEFAULT_TIME);

  const onValueChange = useCallback(() => {
    if (value === undefined) {
      setDateRange(undefined);
      setFromTimeValue(getDefaultTime());
      setToTimeValue(getDefaultTime());
      return;
    }
    
    const rangeValue = value as DateRange;
    setDateRange(rangeValue);
  
    if (rangeValue.from) {
      const h = rangeValue.from.getHours().toString().padStart(2, '0');
      const m = rangeValue.from.getMinutes().toString().padStart(2, '0');
      setFromTimeValue(`${h}:${m}`);
    }
    
    if (rangeValue.to) {
      const h = rangeValue.to.getHours().toString().padStart(2, '0');
      const m = rangeValue.to.getMinutes().toString().padStart(2, '0');
      setToTimeValue(`${h}:${m}`);
    }
  }, [value, getDefaultTime]);

  useEffect(() => {
    onValueChange();
  }, [value, onValueChange]);

  const getDisplayDate = useCallback(() => {
    if (!dateRange?.from) {
      return undefined;
    }
    
    if (dateRange.to) {
      return `${format(dateRange.from, 'PPP')} - ${format(dateRange.to, 'PPP')}`;
    }
    
    return format(dateRange.from, 'PPP');
  }, [dateRange]);

  const handleTimeChange = useCallback(
    ({ time }: DateTimeChange, toTime?: DateTimeChange) => {
      if (!time) return;
      
      if (dateRange?.from && dateRange?.to) {
        let newFromDateObj = dateRange.from;
        let newToDateObj = dateRange.to;
        let newFromValue = fromTimeValue;
        let newToValue = toTimeValue;
        
        if (toTime?.time) {
          const toResult = setSelectedDateTime(dateRange.to, undefined, toTime.time, toTimeValue);
          newToDateObj = toResult.date;
          newToValue = toResult.fromValue;
        } else {
          const fromResult = setSelectedDateTime(dateRange.from, undefined, time, fromTimeValue);
          newFromDateObj = fromResult.date;
          newFromValue = fromResult.fromValue;
          
          const toResult = setSelectedDateTime(dateRange.to, undefined, time, toTimeValue);
          newToDateObj = toResult.date;
          newToValue = toResult.fromValue;
        }
        
        setDateRange({ from: newFromDateObj, to: newToDateObj });
        setFromTimeValue(newFromValue);
        setToTimeValue(newToValue);
        onChange?.({ from: newFromDateObj, to: newToDateObj });
      }
    },
    [dateRange, fromTimeValue, toTimeValue, onChange]
  );

  const handleFromTimeChange = useCallback(
    (time: string) => {
      setFromTimeValue(time);
      if (time && dateRange?.from) {
        const {
          date: newFromDateObj,
          fromValue: newFromValue
        } = setSelectedDateTime(dateRange.from, undefined, time, fromTimeValue);
        
        setDateRange({ ...dateRange, from: newFromDateObj });
        setFromTimeValue(newFromValue);
        onChange?.({ ...dateRange, from: newFromDateObj });
      }
    },
    [dateRange, fromTimeValue, onChange]
  );

  const handleToTimeChange = useCallback(
    (time: string) => {
      setToTimeValue(time);
      if (time && dateRange?.to) {
        const {
          date: newToDateObj,
          fromValue: newToValue
        } = setSelectedDateTime(dateRange.to, undefined, time, toTimeValue);
        
        setDateRange({ ...dateRange, to: newToDateObj });
        setToTimeValue(newToValue);
        onChange?.({ ...dateRange, to: newToDateObj });
      }
    },
    [dateRange, toTimeValue, onChange]
  );

  const handleRangeSelect = (value: DateRange | undefined, selectedDay: Date) => {
    const inferredRange = getDateRange({
      value,
      selectedDay,
      fixedRangeDays,
      shouldExcludeFuture,
      shouldExcludePast,
      disableDatesAfter
    });
    if (inferredRange?.from) {
      const fromResult = setSelectedDateTime(inferredRange.from, undefined, fromTimeValue, fromTimeValue);
      if (inferredRange.to) {
        const toResult = setSelectedDateTime(inferredRange.to, undefined, toTimeValue, toTimeValue);

        const newRange = {
          from: fromResult.date,
          to: toResult.date
        };

        setDateRange(newRange);
        onChange?.(newRange as DateRange);
      } else {
        const toResult = setSelectedDateTime(selectedDay, undefined, toTimeValue, toTimeValue);

        const newRange = {
          from: fromResult.date,
          to: toResult.date
        };

        setDateRange(newRange);
        onChange?.(newRange as DateRange);
      }
    } else {
      setDateRange(inferredRange);
      onChange?.(inferredRange as DateRange);
    }
  };

  const handleClear = () => {
    setDateRange(undefined);
    setFromTimeValue(getDefaultTime());
    setToTimeValue(getDefaultTime());
    onChange?.(undefined);
    onClear?.();
  };

  const handleApply = () => {
    if(dateRange?.from && dateRange?.to) {
        onApply?.(dateRange);
    }
  };

  return {
    value: dateRange,
    getDisplayDate,
    changeTime: handleTimeChange,
    onValueChange,
    select: (value, selectedDay) => handleRangeSelect(value as DateRange | undefined, selectedDay),
    apply: handleApply,
    clear: handleClear,
    isApplyDisabled: !dateRange?.from || !dateRange?.to,
    fromValue: fromTimeValue,
    toValue: toTimeValue,
    onFromTimeChange: handleFromTimeChange,
    onToTimeChange: handleToTimeChange,
  }
};